import { CookiesStorage } from '@Core/Communication/GlobalStore/CookiesStorage/CookiesStorage'
import { getAuth, signOut } from 'firebase/auth'

/** Codes the API returns when it refuses the session itself (contracts/api-identity.md §1). */
export const AUTH_DENIAL_MESSAGES = {
    ACCOUNT_NOT_LINKED:
        'Seu acesso ainda não foi liberado. Fale com um administrador.',
    USER_DISABLED: 'Seu acesso está desativado. Fale com um administrador.',
    PASSWORD_LOGIN_NOT_ALLOWED:
        'O login com senha não está disponível para sua conta. Use Entrar com Microsoft.',
} as const

export type AuthDenialCode = keyof typeof AUTH_DENIAL_MESSAGES

const PENDING_MESSAGE_KEY = '@ofc:authDenialMessage'
const LOGIN_PATH = '/login'

interface ErrorWithResponse {
    response?: { status?: number; data?: { code?: unknown } }
}

export const getAuthDenialCode = (error: unknown): AuthDenialCode | null => {
    const code = (error as ErrorWithResponse)?.response?.data?.code
    return typeof code === 'string' && code in AUTH_DENIAL_MESSAGES
        ? (code as AuthDenialCode)
        : null
}

/** Keeps the message across the redirect so the login screen can show it. */
export const consumeAuthDenialMessage = (): string | null => {
    try {
        const message = sessionStorage.getItem(PENDING_MESSAGE_KEY)
        sessionStorage.removeItem(PENDING_MESSAGE_KEY)
        return message
    } catch {
        return null
    }
}

const endFirebaseSession = () => signOut(getAuth())

/**
 * Ends the local session after the API refused it and sends the user back to
 * the login screen with the reason.
 */
export const handleAuthDenial = async (
    code: AuthDenialCode,
    endSession: () => Promise<void> = endFirebaseSession
) => {
    try {
        sessionStorage.setItem(PENDING_MESSAGE_KEY, AUTH_DENIAL_MESSAGES[code])
    } catch {
        // Private mode: the user still lands on the login screen.
    }
    new CookiesStorage().removeItem('@ofc:accessToken')
    await endSession().catch(() => undefined)
    if (window.location.pathname !== LOGIN_PATH) {
        window.location.assign(LOGIN_PATH)
    }
}
